import { createContext, useContext, useEffect, useMemo } from "react";
import {
  alpha,
  createTheme,
  darken,
  lighten,
  ThemeProvider,
  useTheme,
} from "@mui/material/styles";
import { useSelector } from "react-redux";
import {
  useGetWhiteLabelCompaniesQuery,
  useGetWhiteLabelCompanyRuntimeConfigQuery,
} from "../../../redux/admin/whiteLabelApi";

const DEFAULT_BRAND_TITLE = "Third Eye Telematics";
const DEFAULT_BRAND_SUBTITLE = "Track. Monitor. Protect.";
const DEFAULT_THEME_COLOR = "#1d63c8";
const DEFAULT_SIDEBAR_COLOR = "#16233d";
const DEFAULT_FAVICON_PATH = "/thirdeye-logo.png";

function isThirdEyeDeployment() {
  if (typeof window === "undefined") return false;
  return /(^|\.)thirdeyetelematics\.in$/i.test(window.location.hostname);
}

const WhiteLabelRuntimeContext = createContext({
  brandTitle: DEFAULT_BRAND_TITLE,
  brandSubtitle: DEFAULT_BRAND_SUBTITLE,
  logoUrl: "",
  faviconUrl: "",
  company: null,
  config: null,
  isApplied: false,
});

function normalizeText(value) {
  return String(value || "").trim();
}

function normalizeLookupKey(value) {
  return normalizeText(value).toLowerCase();
}

function coerceCssColor(value, fallback) {
  const nextValue = normalizeText(value);
  return nextValue || fallback;
}

function applyRootCssVariable(name, value) {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(name, value);
}

function updateFavicon(nextHref) {
  if (typeof document === "undefined" || !nextHref) return;

  [
    'link[rel="icon"]',
    'link[rel="shortcut icon"]',
    'link[rel="apple-touch-icon"]',
  ].forEach((selector) => {
    const element = document.head.querySelector(selector);
    if (element) {
      element.setAttribute("href", nextHref);
    }
  });
}

function findMatchedCompany(companies, user) {
  const candidateKeys = [user?.clientName, user?.companyCode, user?.companyName]
    .map(normalizeLookupKey)
    .filter(Boolean);

  if (!candidateKeys.length || !Array.isArray(companies)) {
    return null;
  }

  return (
    companies.find((company) => {
      const companyNameKey = normalizeLookupKey(company?.companyName);
      const companyCodeKey = normalizeLookupKey(company?.companyCode);
      return candidateKeys.some(
        (candidate) => candidate === companyNameKey || candidate === companyCodeKey
      );
    }) || null
  );
}

function buildRuntimeTheme(baseTheme, runtimeConfig) {
  const branding = runtimeConfig?.branding || null;
  const runtimeTheme = runtimeConfig?.theme || null;
  const colorConfig = runtimeTheme?.colors || {};
  const typographyConfig = runtimeTheme?.typography || {};
  const layoutConfig = runtimeTheme?.layout || {};

  const primaryMain = coerceCssColor(
    branding?.primaryColor || colorConfig.primary,
    baseTheme.palette.primary.main || DEFAULT_THEME_COLOR
  );
  const secondaryMain = coerceCssColor(
    branding?.secondaryColor || colorConfig.secondary,
    baseTheme.palette.secondary?.main || darken(primaryMain, 0.18)
  );
  const accentMain = coerceCssColor(
    branding?.accentColor || colorConfig.accent,
    lighten(primaryMain, 0.14)
  );
  const successMain = coerceCssColor(colorConfig.success, baseTheme.palette.success.main);
  const warningMain = coerceCssColor(colorConfig.warning, baseTheme.palette.warning.main);
  const errorMain = coerceCssColor(colorConfig.error, baseTheme.palette.error.main);
  const backgroundDefault = coerceCssColor(
    colorConfig.background,
    baseTheme.palette.background.default
  );
  const backgroundPaper = coerceCssColor(
    colorConfig.cardBackground,
    baseTheme.palette.background.paper
  );
  const textPrimary = coerceCssColor(colorConfig.textPrimary, baseTheme.palette.text.primary);
  const textSecondary = coerceCssColor(
    colorConfig.textSecondary,
    baseTheme.palette.text.secondary
  );
  const dividerColor = coerceCssColor(colorConfig.border, baseTheme.palette.divider);
  const fontFamily = normalizeText(typographyConfig.fontFamily) || baseTheme.typography.fontFamily;
  const borderRadius = Number(layoutConfig.borderRadius);

  return createTheme(baseTheme, {
    palette: {
      ...baseTheme.palette,
      primary: {
        ...baseTheme.palette.primary,
        main: primaryMain,
        light: lighten(primaryMain, 0.24),
        dark: darken(primaryMain, 0.18),
        contrastText: "#ffffff",
      },
      secondary: {
        ...(baseTheme.palette.secondary || {}),
        main: secondaryMain,
        light: lighten(secondaryMain, 0.2),
        dark: darken(secondaryMain, 0.18),
        contrastText: "#ffffff",
      },
      success: {
        ...baseTheme.palette.success,
        main: successMain,
      },
      warning: {
        ...baseTheme.palette.warning,
        main: warningMain,
      },
      error: {
        ...baseTheme.palette.error,
        main: errorMain,
      },
      text: {
        ...baseTheme.palette.text,
        primary: textPrimary,
        secondary: textSecondary,
      },
      divider: dividerColor,
      background: {
        ...baseTheme.palette.background,
        default: backgroundDefault,
        paper: backgroundPaper,
      },
    },
    shape: {
      ...baseTheme.shape,
      borderRadius: Number.isFinite(borderRadius) ? borderRadius : baseTheme.shape.borderRadius,
    },
    typography: {
      ...baseTheme.typography,
      fontFamily,
    },
    components: {
      ...baseTheme.components,
      MuiTooltip: {
        ...baseTheme.components?.MuiTooltip,
        styleOverrides: {
          ...(baseTheme.components?.MuiTooltip?.styleOverrides || {}),
          tooltip: {
            ...(baseTheme.components?.MuiTooltip?.styleOverrides?.tooltip || {}),
            background: `linear-gradient(135deg, ${primaryMain} 0%, ${darken(primaryMain, 0.18)} 100%)`,
            border: `1px solid ${alpha(lighten(primaryMain, 0.18), 0.28)}`,
            boxShadow: `0 10px 24px ${alpha(primaryMain, 0.22)}`,
          },
          arrow: {
            ...(baseTheme.components?.MuiTooltip?.styleOverrides?.arrow || {}),
            color: darken(primaryMain, 0.18),
          },
        },
      },
    },
    customWhiteLabel: {
      accentMain,
      sidebarColor: coerceCssColor(colorConfig.sidebar, DEFAULT_SIDEBAR_COLOR),
      headerColor: coerceCssColor(colorConfig.header, backgroundPaper),
    },
  });
}

export default function WhiteLabelRuntimeProvider({ children }) {
  const baseTheme = useTheme();
  const currentUser = useSelector((state) => state.auth.user);
  const companyLookupSearch = normalizeText(currentUser?.clientName);

  const { data: companyListResult, isError: isCompanyLookupError } =
    useGetWhiteLabelCompaniesQuery(
      {
        search: companyLookupSearch,
        limit: 25,
        sortBy: "companyName",
        sortOrder: "asc",
      },
      {
        skip: !companyLookupSearch,
      }
    );

  const resolvedCompany = useMemo(
    () => findMatchedCompany(companyListResult?.items || [], currentUser),
    [companyListResult?.items, currentUser]
  );

  const { data: runtimeConfig } = useGetWhiteLabelCompanyRuntimeConfigQuery(
    resolvedCompany?._id,
    {
      skip: !resolvedCompany?._id || isCompanyLookupError,
    }
  );

  const runtimeTheme = useMemo(
    () => buildRuntimeTheme(baseTheme, runtimeConfig),
    [baseTheme, runtimeConfig]
  );

  const runtimeValue = useMemo(() => {
    const branding = runtimeConfig?.branding || null;
    const company = runtimeConfig?.company || null;
    const website = runtimeConfig?.website || null;

    // The dedicated Third Eye deployment is not a customer white-label site.
    // Keep its CRM title, logo and tab icon consistent even if the logged-in
    // account has a legacy white-label record (for example, GPS Controller).
    if (isThirdEyeDeployment()) {
      return {
        brandTitle: DEFAULT_BRAND_TITLE,
        brandSubtitle: DEFAULT_BRAND_SUBTITLE,
        logoUrl: "",
        faviconUrl: DEFAULT_FAVICON_PATH,
        company,
        config: runtimeConfig || null,
        isApplied: true,
      };
    }

    return {
      brandTitle:
        normalizeText(branding?.productName) ||
        normalizeText(branding?.companyName) ||
        normalizeText(company?.name) ||
        DEFAULT_BRAND_TITLE,
      brandSubtitle:
        normalizeText(company?.description) ||
        normalizeText(website?.title) ||
        DEFAULT_BRAND_SUBTITLE,
      logoUrl:
        normalizeText(branding?.logo?.url) ||
        normalizeText(branding?.loginLogo?.url) ||
        "",
      faviconUrl:
        normalizeText(branding?.favicon?.url) ||
        normalizeText(website?.seo?.favicon?.url) ||
        "",
      company,
      config: runtimeConfig || null,
      isApplied: Boolean(runtimeConfig && resolvedCompany),
    };
  }, [resolvedCompany, runtimeConfig]);

  useEffect(() => {
    const themeColor = runtimeTheme?.palette?.primary?.main || DEFAULT_THEME_COLOR;
    const accentColor =
      runtimeTheme?.customWhiteLabel?.accentMain || lighten(themeColor, 0.14);
    const sidebarColor =
      runtimeTheme?.customWhiteLabel?.sidebarColor || DEFAULT_SIDEBAR_COLOR;
    const headerColor =
      runtimeTheme?.customWhiteLabel?.headerColor ||
      runtimeTheme?.palette?.background?.paper ||
      "#ffffff";

    applyRootCssVariable("--primary-color", themeColor);
    applyRootCssVariable("--logo-blue", themeColor);
    applyRootCssVariable("--logo-blue-hover", darken(themeColor, 0.18));
    applyRootCssVariable("--logo-blue-soft", lighten(themeColor, 0.24));
    applyRootCssVariable("--logo-blue-tint", alpha(themeColor, 0.1));
    applyRootCssVariable("--logo-blue-border", alpha(themeColor, 0.22));
    applyRootCssVariable("--logo-blue-shadow", alpha(themeColor, 0.22));
    applyRootCssVariable("--runtime-accent", accentColor);
    applyRootCssVariable("--runtime-sidebar-bg", sidebarColor);
    applyRootCssVariable("--runtime-sidebar-bg-alt", darken(sidebarColor, 0.08));
    applyRootCssVariable("--runtime-sidebar-text", alpha("#ffffff", 0.92));
    applyRootCssVariable("--runtime-sidebar-muted", alpha("#d9e5ff", 0.64));
    applyRootCssVariable("--runtime-sidebar-active", themeColor);
    applyRootCssVariable("--runtime-sidebar-active-shadow", alpha(themeColor, 0.24));
    applyRootCssVariable("--runtime-header-bg", headerColor);

    if (typeof document !== "undefined") {
      document.title = runtimeValue.brandTitle || DEFAULT_BRAND_TITLE;

      const themeMeta = document.querySelector('meta[name="theme-color"]');
      if (themeMeta) {
        themeMeta.setAttribute("content", themeColor);
      }

      const appleTitleMeta = document.querySelector(
        'meta[name="apple-mobile-web-app-title"]'
      );
      if (appleTitleMeta) {
        appleTitleMeta.setAttribute("content", runtimeValue.brandTitle || DEFAULT_BRAND_TITLE);
      }

      updateFavicon(runtimeValue.faviconUrl || DEFAULT_FAVICON_PATH);
    }
  }, [runtimeTheme, runtimeValue.brandTitle, runtimeValue.faviconUrl]);

  return (
    <WhiteLabelRuntimeContext.Provider value={runtimeValue}>
      <ThemeProvider theme={runtimeTheme}>{children}</ThemeProvider>
    </WhiteLabelRuntimeContext.Provider>
  );
}

export function useWhiteLabelRuntime() {
  return useContext(WhiteLabelRuntimeContext);
}
